/* eslint-disable */

// Memory and CPU friendly SHA256 algorithm with MSRCrypto core
// Max 2^32 bits = 536MB for single string, quite enough

// SHA-256 constants
const K = new Int32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
  0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
  0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
  0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
  0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
  0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const H = new Int32Array([
  0x6A09E667,
  0xBB67AE85,
  0x3C6EF372,
  0xA54FF53A,
  0x510E527F,
  0x9B05688C,
  0x1F83D9AB,
  0x5BE0CD19,
]);

const paddingStrings = [
  "00000000",
  "0000000",
  "000000",
  "00000",
  "0000",
  "000",
  "00",
  "0",
  "",
];

// Fill buffer with zero, range is [start, end]
const fillZeroToBuffer = function (buffer, start, end) {
  for (let idx = start; idx <= end; idx += 1) {
    buffer[idx] = 0;
  }
};

// Adapter for string to buffer, private class
export class DataAdaptor {
  rebind(source, sharedByteCache) {
    this.source = source;
    this.sourceLength = source.length;

    this.charCodeIndex = 0;

    // Give a cache of 128 byte (2*512 bit)
    this.byteCache = sharedByteCache;
    this.cacheSize = 0;

    this.sumBytes = 0;

    this.willAddExtraChunk = false;
    this.willAppendLeadingOne = true;
    this.hasDone = false;
  }

  safeWriteTail(buffer) {
    buffer[14] = 0;
    buffer[15] = this.sumBytes << 3;
  }

  getNextChunk(chunk) {
    // Chunk only use first 512 bits = 64 bytes
    if (this.hasDone) {
      return false;
    }

    if (this.willAddExtraChunk) {
      if (this.willAppendLeadingOne) {
        chunk[0] = 0x80000000;
      }
      fillZeroToBuffer(chunk, (this.willAppendLeadingOne ? 1 : 0), 13);
      this.safeWriteTail(chunk);
      this.hasDone = true;
      return true;
    }

    let char,
      char2,
      idx,
      idx2,
      temp,
      ringCache = this.byteCache,
      cacheSize = this.cacheSize;

    while (cacheSize < 64 && this.charCodeIndex < this.sourceLength) { // cachesize < 64
      char = this.source.charCodeAt(this.charCodeIndex++);

      if (char < 128) {
        ringCache[(cacheSize++) & 0x7F] = char;
      } else if (char < 2048) {
        ringCache[(cacheSize++) & 0x7F] = (char >> 6) | 192;
        ringCache[(cacheSize++) & 0x7F] = (char & 63) | 128;
      } else if ((char & 0xFC00) == 0xD800 && (this.charCodeIndex < this.sourceLength) && (char2 = this.source.charCodeAt(this.charCodeIndex)) && ((char2 & 0xFC00) == 0xDC00)) {
        this.charCodeIndex += 1;
        char = 0x10000 + ((char & 0x03FF) << 10) + (char2 & 0x03FF);
        ringCache[(cacheSize++) & 0x7F] = (char >> 18) | 240;
        ringCache[(cacheSize++) & 0x7F] = ((char >> 12) & 63) | 128;
        ringCache[(cacheSize++) & 0x7F] = ((char >> 6) & 63) | 128;
        ringCache[(cacheSize++) & 0x7F] = (char & 63) | 128;
      } else {
        ringCache[(cacheSize++) & 0x7F] = (char >> 12) | 224;
        ringCache[(cacheSize++) & 0x7F] = ((char >> 6) & 63) | 128;
        ringCache[(cacheSize++) & 0x7F] = (char & 63) | 128;
      }
    }

    // cacheSize >= 64
    if (cacheSize >>> 6) {
      for (idx = 0; idx < 16; idx += 1) {
        temp = (ringCache[idx << 2] << 24) ^ (ringCache[idx << 2 ^ 1] << 16) ^ (ringCache[idx << 2 ^ 2] << 8) ^ (ringCache[idx << 2 ^ 3]);
        chunk[idx] = temp;
      }
      for (idx = 64; idx < cacheSize; idx += 1) {
        ringCache[idx & 0x3F] = ringCache[idx];
      }
      this.sumBytes += 64;
      this.cacheSize = cacheSize - 64;
    } else {
      for (idx = 0; idx < cacheSize >> 2; idx += 1) {
        temp = (ringCache[idx << 2] << 24) + (ringCache[idx << 2 ^ 1] << 16) + (ringCache[idx << 2 ^ 2] << 8) + (ringCache[idx << 2 ^ 3]);
        chunk[idx] = temp;
      }

      for (temp = 0, idx2 = 0; idx2 < 4; idx2 += 1) {
        temp <<= 8;
        if ((idx << 2) + idx2 < cacheSize) {
          temp ^= ringCache[(idx << 2) + idx2];
        } else if (this.willAppendLeadingOne) {
          temp ^= 0x80;
          this.willAppendLeadingOne = false;
        }
      }

      chunk[idx++] = temp;

      this.sumBytes += cacheSize;

      // Not enough strings, may add extra chunk or not
      if (cacheSize <= 55) {
        // Will add tail this chunk
        fillZeroToBuffer(chunk, idx, 13);
        this.safeWriteTail(chunk);
        this.hasDone = true;
      } else {
        fillZeroToBuffer(chunk, idx, 15);
        this.willAddExtraChunk = true;
      }
      this.cacheSize = 0;
    }

    return true;
  }
}

export class OptimizedSHA256 {
  constructor() {
    this.dataAdaptor = new DataAdaptor();
    this.sharedMemory = new Int32Array(new ArrayBuffer(4 * 64));
    this.sharedByteCache = new Uint8Array(new ArrayBuffer(128));
    this.sharedH = new Int32Array(new ArrayBuffer(4 * 8));
  }

  clearSharedStates() {
    for (let idx = 0; idx < 8; idx += 1) {
      this.sharedH[idx] = H[idx];
    }
    fillZeroToBuffer(this.sharedMemory, 0, 64);
  }

  // Iterate H and W
  hashChunk(h, w) {
    let t,
      i,
      temp,
      x0,
      x1,
      mask = 0xFFFFFFFF;

    let ra = h[0],
      rb = h[1],
      rc = h[2],
      rd = h[3],
      re = h[4],
      rf = h[5],
      rg = h[6],
      rh = h[7];

    // 16 ≤ t ≤ 63
    for (t = 16; t < 64; t++) {
      x0 = w[t - 15];
      x1 = w[t - 2];

      w[t] = ((((x1 >>> 17) | (x1 << 15)) ^ ((x1 >>> 19) | (x1 << 13)) ^ (x1 >>> 10))
                + w[t - 7]
                + (((x0 >>> 7) | (x0 << 25)) ^ ((x0 >>> 18) | (x0 << 14)) ^ (x0 >>> 3))
                + w[t - 16])
              & mask;
    }

    for (i = 0; i < 64; i++) {
      temp = rh +
                ((re >>> 6 | re << 26) ^ (re >>> 11 | re << 21) ^ (re >>> 25 | re << 7)) +
                ((re & rf) ^ ((~re) & rg)) +
                K[i] + w[i];

      rd += temp;

      temp += ((ra >>> 2 | ra << 30) ^ (ra >>> 13 | ra << 19) ^ (ra >>> 22 | ra << 10)) +
                ((ra & (rb ^ rc)) ^ (rb & rc));

      rh = rg; // 'h' = g
      rg = rf; // 'g' = f
      rf = re; // 'f' = e
      re = rd; // 'e' = d
      rd = rc; // 'd' = c
      rc = rb; // 'c' = b
      rb = ra; // 'b' = a
      ra = temp; // 'a' = temp
    }

    // Update the hash values
    h[0] += ra & mask;
    h[1] += rb & mask;
    h[2] += rc & mask;
    h[3] += rd & mask;
    h[4] += re & mask;
    h[5] += rf & mask;
    h[6] += rg & mask;
    h[7] += rh & mask;
  }

  hexDigest(hashResult) {
    // Convert H to string
    const temp = [];
    for (let idx = 0; idx < 8; idx += 1) {
      const digstr = (hashResult[idx] >>> 0).toString(16);
      temp.push(paddingStrings[Math.min(8, digstr.length)]);
      temp.push(digstr);
    }

    return temp.join('');
  }

  hashString(str) {
    this.clearSharedStates();

    // Rebind adaptor
    this.dataAdaptor.rebind(str, this.sharedByteCache);

    // Iterate
    while (this.dataAdaptor.getNextChunk(this.sharedMemory)) {
      this.hashChunk(this.sharedH, this.sharedMemory);
    }

    return this.hexDigest(this.sharedH);
  }
}
